import React from 'react';
import { FileSignature, Check, Plus } from 'lucide-react';

/**
 * Quanti contenuti prevede il contratto del cliente (X a settimana / X al mese)
 * e quanti ne mancano nel Piano Editoriale. Dati da GET /api/ped/copertura.
 * Contano post, reel e caroselli con data nel periodo (storie, campagne e scartati esclusi).
 */

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const pad2 = (n) => String(n).padStart(2, '0');
const oggiStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

export const descriviContratto = (quota, periodo) =>
  quota ? `${quota} contenut${quota === 1 ? 'o' : 'i'} ${periodo === 'settimana' ? 'a settimana' : 'al mese'}` : '';

// Colore di un periodo: completo verde, in corso giallo, chiuso con buchi rosso
function tono(p) {
  if (!p.mancanti) return { c: 'var(--green)', bg: 'var(--green-dim)' };
  if (p.passato) return { c: 'var(--red)', bg: 'var(--red-dim)' };
  return { c: 'var(--yellow)', bg: 'var(--yellow-dim)' };
}

function Barra({ valore, totale, colore, alto = 6 }) {
  const pct = totale ? Math.min(100, Math.round((valore / totale) * 100)) : 0;
  return (
    <div style={{ height: alto, borderRadius: alto, background: 'var(--bg3)', overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: colore, borderRadius: alto, transition: 'width 0.3s var(--ease)' }} />
    </div>
  );
}

/** Pallino "−4" / "✓" per i selettori cliente. */
export function BadgeCopertura({ cop }) {
  if (!cop) return null;
  const manca = cop.totale.mancanti;
  const t = manca ? { c: 'var(--yellow)', bg: 'var(--yellow-dim)' } : { c: 'var(--green)', bg: 'var(--green-dim)' };
  return (
    <span title={manca ? `Mancano ${manca} contenuti nel mese (contratto: ${descriviContratto(cop.quota, cop.periodo)})` : 'Contratto coperto per il mese'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, color: t.c, background: t.bg, fontVariantNumeric: 'tabular-nums' }}>
      {manca ? `−${manca}` : <Check size={11} strokeWidth={3} />}
    </span>
  );
}

/**
 * Riquadro nel Piano Editoriale. onNew(dataStr) apre un nuovo contenuto in quel periodo.
 */
export default function CoperturaContratto({ cop, onNew, style }) {
  if (!cop) return null;
  const [y, m] = cop.mese.split('-').map(Number);
  const mese = MESI[m - 1];
  const tot = cop.totale;
  const settimanale = cop.periodo === 'settimana';
  const oggi = oggiStr();
  const giornoNuovo = (p) => (p.da > oggi ? p.da : oggi <= p.a ? oggi : null);
  const extra = Math.max(0, tot.previsti - tot.quota);

  const titolo = tot.mancanti
    ? <>Mancano <b style={{ color: 'var(--text)' }}>{tot.mancanti}</b> contenut{tot.mancanti === 1 ? 'o' : 'i'} a {mese}</>
    : <>{mese.charAt(0).toUpperCase() + mese.slice(1)} coperto{extra ? ` (+${extra} in più)` : ''}</>;

  return (
    <section className="eden-card" style={{ padding: '14px 16px', marginBottom: 14, ...style }} aria-label="Contenuti da contratto">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: tot.mancanti ? 'var(--yellow-dim)' : 'var(--green-dim)', color: tot.mancanti ? 'var(--yellow)' : 'var(--green)' }}>
          {tot.mancanti ? <FileSignature size={16} /> : <Check size={17} strokeWidth={2.6} />}
        </span>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: tot.mancanti ? 'var(--text-muted)' : 'var(--text)' }}>{titolo}</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 1 }}>
            Contratto: {descriviContratto(cop.quota, cop.periodo)} · {tot.previsti} di {tot.quota} in piano{tot.pubblicati ? ` · ${tot.pubblicati} pubblicat${tot.pubblicati === 1 ? 'o' : 'i'}` : ''}
            {y !== new Date().getFullYear() ? ` · ${y}` : ''}
          </div>
        </div>
        {!settimanale && tot.mancanti > 0 && onNew && giornoNuovo(cop.periodi[0]) && (
          <button className="eden-btn is-sm" onClick={() => onNew(giornoNuovo(cop.periodi[0]))}><Plus size={14} /> Aggiungi</button>
        )}
      </div>

      {!settimanale && (
        <div style={{ marginTop: 11 }}>
          <Barra valore={tot.previsti} totale={tot.quota} colore={tot.mancanti ? 'var(--yellow)' : 'var(--green)'} />
        </div>
      )}

      {settimanale && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 8, marginTop: 12 }}>
          {cop.periodi.map(p => {
            const t = tono(p);
            const giorno = p.mancanti && onNew ? giornoNuovo(p) : null;
            const Tag = giorno ? 'button' : 'div';
            return (
              <Tag key={p.da} type={giorno ? 'button' : undefined} onClick={giorno ? () => onNew(giorno) : undefined}
                title={giorno ? `Aggiungi un contenuto nella settimana ${p.label}` : undefined}
                style={{ textAlign: 'left', font: 'inherit', padding: '9px 11px', borderRadius: 10, cursor: giorno ? 'pointer' : 'default',
                  background: 'var(--bg2)', border: `1px solid ${p.corrente ? 'var(--cyan)' : 'var(--border)'}`, opacity: p.passato && !p.mancanti ? 0.7 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: p.corrente ? 'var(--cyan)' : 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                    {p.corrente ? 'Questa sett.' : p.label}
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{p.previsti}/{p.quota}</span>
                </div>
                <div style={{ margin: '7px 0 6px' }}><Barra valore={p.previsti} totale={p.quota} colore={t.c} alto={5} /></div>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: t.c, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {p.mancanti
                    ? <>{p.passato ? `${p.mancanti} non fatt${p.mancanti === 1 ? 'o' : 'i'}` : `Manca${p.mancanti === 1 ? '' : 'no'} ${p.mancanti}`}{giorno && <Plus size={12} style={{ marginLeft: 'auto' }} />}</>
                    : <><Check size={12} strokeWidth={2.8} /> Coperta{p.extra ? ` (+${p.extra})` : ''}</>}
                </div>
              </Tag>
            );
          })}
        </div>
      )}
    </section>
  );
}
